import { useMemo, useState } from "react";

import * as _ from "lodash";

import { SearchInput, PlainTooltip } from "@similarweb/ui-components";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { SWReactTable } from "app/components/React/Table/SWReactTable";
import { i18nFilter } from "app/filters/i18nFilter";
import { categoryLoyaltyTableColumnsConfigGen } from "app/pages/industry-analysis/category-loyalty/CategoryLoyaltyTableConfig";
import {
    StyledSearchContainer,
    StyledTableWrapper,
    ExcelButtonContainer,
} from "app/pages/industry-analysis/category-loyalty/StyledComponents";
import { DefaultFetchService } from "app/services/fetchService";

import { TableTopContainer } from "../geography/components/TableSearchAndDownloadHeader";

const EXCEL_ENDPOINT = "api/category/audienceLoyalty/Excel";

interface ICategoryLoyaltyTableContainerProps {
    tableData: any;
    isLoading?: boolean;
    pageFilters: any;
}

interface ISortColumn {
    field: string;
    sortDirection: "desc" | "asc";
    customSort?: (a, b) => number;
}

export const CategoryLoyaltyTableContainer = (props: ICategoryLoyaltyTableContainerProps) => {
    const { isLoading, tableData, pageFilters } = props;

    const [searchString, setSearchString] = useState<string>("");
    const [sortedColumn, setSortedColumn] = useState<ISortColumn>({
        field: "Share",
        sortDirection: "desc",
        customSort: undefined,
    });

    const onSearch = (val) => {
        setSearchString(val.trim());
    };

    // eslint-disable-next-line no-shadow-restricted-names
    const onSort = ({ field, sortDirection }, undefined, column) => {
        setSortedColumn({
            field,
            sortDirection,
            customSort: column.customSort,
        });
    };

    const tableExcelApiUrl = () => {
        const categoryObject = categoryService.categoryQueryParamToCategoryObject(pageFilters.category);
        const fetcherService = DefaultFetchService.getInstance();
        const apiParams: any = {
            includeSubDomains: !pageFilters.isWWW,
            from: pageFilters.from,
            to: pageFilters.to,
            country: pageFilters.country,
            webSource: pageFilters.webSource,
            isWindow: pageFilters.isWindow,
            keys: categoryObject?.forApi,
            category: categoryObject?.forDisplayApi,
            categoryHash: categoryObject?.categoryHash,
            sort: sortedColumn.field,
            asc: sortedColumn.sortDirection === "asc",
        };
        return `${EXCEL_ENDPOINT}?${fetcherService.requestParams(apiParams)}`;
    };
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const tableColumns = useMemo<any>(() => {
        return categoryLoyaltyTableColumnsConfigGen(sortedColumn.field, sortedColumn.sortDirection);
    }, [sortedColumn]);

    const render = () => {
        let filteredData = [];
        if (!isLoading && tableData) {
            filteredData =
                !isLoading && searchString !== ""
                    ? _.filter(tableData, (dataItem: any) => {
                          return dataItem.Domain.toLowerCase().indexOf(searchString.toLowerCase()) !== -1;
                      })
                    : tableData;
            filteredData = sortedColumn.customSort
                ? sortedColumn.sortDirection === "asc"
                    ? filteredData.sort(sortedColumn.customSort)
                    : filteredData.sort(sortedColumn.customSort).reverse()
                : _.orderBy(filteredData, sortedColumn.field, sortedColumn.sortDirection);
        }

        const tableProps = {
            onSort,
            isLoading,
            tableData: { Data: filteredData, TotalCount: tableData?.length },
            tableColumns: tableColumns,
            addPaddingRightCell: true,
        };

        return (
            <StyledTableWrapper loading={isLoading}>
                <TableTopContainer>
                    <StyledSearchContainer>
                        <SearchInput
                            defaultValue={searchString}
                            debounce={100}
                            onChange={onSearch}
                            placeholder={i18nFilter()("category.analysis.loyalty.table.search.placeholder")}
                        />
                    </StyledSearchContainer>

                    <PlainTooltip tooltipContent={i18nFilter()("downloadCSV")}>
                        <ExcelButtonContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                downloadUrl={tableExcelApiUrl()}
                                excelLocked={!downloadExcelPermitted}
                            />
                        </ExcelButtonContainer>
                    </PlainTooltip>
                </TableTopContainer>
                <SWReactTable {...tableProps} />
            </StyledTableWrapper>
        );
    };

    return render();
};
